import { memo, useCallback, useRef, useState } from "react";
import type { TextInput } from "react-native";
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import type { BottomSheetBackdropProps } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { View, Text, Button } from "@src/components";
import { phoneFormatter } from "@src/components/FormInput";
import { BottomSheetFormInput } from "@src/components/FormInput/BottomSheetFormInput";
import { Checkbox } from "@src/components/Checkbox";

export type CreateSiteWideContactData = {
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
};

interface CreateSiteWideContactBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  onSave: (data: CreateSiteWideContactData) => void;
  isSaving?: boolean;
}

export const CreateSiteWideContactBottomSheet = memo(
  ({ bottomSheetRef, onSave, isSaving }: CreateSiteWideContactBottomSheetProps) => {
    const { theme } = useUnistyles();
    const { bottom } = useSafeAreaInsets();

    // Refs for input values — avoid re-renders on every keystroke
    const firstNameRef = useRef("");
    const lastNameRef = useRef("");
    const emailRef = useRef("");
    const phoneNumberRef = useRef("");

    // Refs to native inputs for clearing on reset
    const firstNameInputRef = useRef<TextInput>(null);
    const lastNameInputRef = useRef<TextInput>(null);
    const emailInputRef = useRef<TextInput>(null);
    const phoneInputRef = useRef<TextInput>(null);

    // State only for things that affect the rendered UI
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [errors, setErrors] = useState<{ firstName?: string; email?: string; phoneNumber?: string }>({});

    const resetForm = useCallback(() => {
      firstNameRef.current = "";
      lastNameRef.current = "";
      emailRef.current = "";
      phoneNumberRef.current = "";
      firstNameInputRef.current?.clear();
      lastNameInputRef.current?.clear();
      emailInputRef.current?.clear();
      phoneInputRef.current?.clear();
      setEmailEnabled(true);
      setSmsEnabled(false);
      setErrors({});
    }, []);

    const handleFirstNameChange = useCallback((value: string) => {
      firstNameRef.current = value;
      if (value.trim()) setErrors((prev) => (prev.firstName ? { ...prev, firstName: undefined } : prev));
    }, []);

    const handleLastNameChange = useCallback((value: string) => {
      lastNameRef.current = value;
    }, []);

    const handleEmailChange = useCallback((value: string) => {
      emailRef.current = value;
      if (value.trim()) setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
    }, []);

    const handlePhoneChange = useCallback((value: string) => {
      phoneNumberRef.current = value;
      if (value.trim()) setErrors((prev) => (prev.phoneNumber ? { ...prev, phoneNumber: undefined } : prev));
    }, []);

    const handleSave = useCallback(() => {
      const firstName = firstNameRef.current;
      const lastName = lastNameRef.current;
      const email = emailRef.current;
      const phoneNumber = phoneNumberRef.current;

      const newErrors: { firstName?: string; email?: string; phoneNumber?: string } = {};

      if (!firstName.trim()) newErrors.firstName = "First name is required";
      if (emailEnabled && !email.trim()) newErrors.email = "Email address is required";
      if (smsEnabled && !phoneNumber.trim()) {
        newErrors.phoneNumber = "Mobile number is required";
      } else if (smsEnabled && !/^\+\d{6,15}$/.test(phoneNumber.trim())) {
        newErrors.phoneNumber = "Use international format, e.g. +61123456789";
      }
      if (!emailEnabled && !smsEnabled) {
        newErrors.email = "Select at least one contact method";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: emailEnabled ? email.trim() : undefined,
        phoneNumber: smsEnabled ? phoneNumber.trim() : undefined,
      });
    }, [emailEnabled, smsEnabled, onSave]);

    const renderBackdrop = useCallback(
      (props: BottomSheetBackdropProps) => <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} />,
      [],
    );

    return (
      <BottomSheetModal
        ref={bottomSheetRef}
        enableDynamicSizing
        enablePanDownToClose
        keyboardBehavior="interactive"
        keyboardBlurBehavior="restore"
        android_keyboardInputMode="adjustResize"
        backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
        backdropComponent={renderBackdrop}
        handleIndicatorStyle={{ backgroundColor: theme.colors.field.border_default, width: 46 }}
        handleStyle={{ paddingTop: theme.spacing["6xl"] }}
        onDismiss={resetForm}
      >
        <BottomSheetScrollView
          contentContainerStyle={{
            paddingBottom: bottom + theme.spacing["5xl"],
            paddingLeft: theme.spacing["5xl"],
            paddingRight: theme.spacing["5xl"],
          }}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
        >
          <Text textStyle="md/medium" mb="md">
            Create site-wide contact
          </Text>
          <Text textStyle="xs/regular" color="textMuted" mb="4xl">
            This contact will receive notifications for all cameras on the site.
          </Text>

          <View gap="3xl">
            <BottomSheetFormInput
              ref={firstNameInputRef}
              placeholder="First name"
              onChangeText={handleFirstNameChange}
              size="medium"
              autoCapitalize="words"
              error={errors.firstName}
            />
            <BottomSheetFormInput
              ref={lastNameInputRef}
              placeholder="Last name"
              onChangeText={handleLastNameChange}
              size="medium"
              autoCapitalize="words"
            />

            {/* Email method */}
            <View gap="lg">
              <View flexDirection="row" alignItems="center" gap="md">
                <Checkbox checked={emailEnabled} onToggle={(checked) => setEmailEnabled(checked)} />
                <Text textStyle="sm/regular">Email</Text>
              </View>
              {emailEnabled && (
                <BottomSheetFormInput
                  ref={emailInputRef}
                  placeholder="Email address"
                  onChangeText={handleEmailChange}
                  size="medium"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  error={errors.email}
                />
              )}
            </View>

            {/* SMS method */}
            <View gap="lg">
              <View flexDirection="row" alignItems="center" gap="md">
                <Checkbox checked={smsEnabled} onToggle={(checked) => setSmsEnabled(checked)} />
                <Text textStyle="sm/regular">SMS</Text>
              </View>
              {smsEnabled && (
                <BottomSheetFormInput
                  ref={phoneInputRef}
                  placeholder="Mobile number"
                  onChangeText={handlePhoneChange}
                  size="medium"
                  formatter={phoneFormatter}
                  error={errors.phoneNumber}
                />
              )}
            </View>
          </View>

          <View mt="4xl">
            <Button variant="primary" label="Save" size="md" onPress={handleSave} loading={isSaving} />
          </View>
        </BottomSheetScrollView>
      </BottomSheetModal>
    );
  },
);

CreateSiteWideContactBottomSheet.displayName = "CreateSiteWideContactBottomSheet";

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
  },
}));
